import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// ─── Helper email (même pattern que FFMCommandes) ────────────────────────────
async function sendEmail({ to, subject, html }) {
  if (process.env.NODE_ENV === 'development') {
    console.log('📧 [DEV] Email simulé →', to);
    console.log('   Sujet :', subject);
    console.log('   Corps :', html);
    return { ok: true };
  }
  const res = await fetch('/api/send-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, html }),
  });
  return res;
}

// ─── Génère un code OTP 6 chiffres ──────────────────────────────────────────
function genererCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#003189',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    padding: '24px',
  },
  logoWrap: {
    marginBottom: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    height: '140px',
    objectFit: 'contain',
  },
  title: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '500',
    letterSpacing: '2px',
    textAlign: 'center',
    opacity: 0.75,
    marginBottom: '6px',
  },
  logoImpulse: {
    height: '56px',
    objectFit: 'contain',
    opacity: 0.9,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: '6px',
  },
  cardSub: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '28px',
    lineHeight: '1.5',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: '1.5px solid #D0D8EE',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    backgroundColor: '#f9fafc',
    transition: 'border-color 0.2s',
  },
  inputOtp: {
    width: '100%',
    padding: '14px',
    fontSize: '26px',
    fontWeight: '700',
    letterSpacing: '10px',
    textAlign: 'center',
    border: '1.5px solid #D0D8EE',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#003189',
    backgroundColor: '#f0f4ff',
  },
  btn: {
    width: '100%',
    padding: '13px',
    marginTop: '20px',
    backgroundColor: '#003189',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    letterSpacing: '0.3px',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  btnSecondary: {
    background: 'none',
    border: 'none',
    color: '#003189',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '14px',
    textDecoration: 'underline',
    padding: 0,
  },
  erreur: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#dc2626',
    fontSize: '13px',
    marginTop: '14px',
  },
  success: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#16a34a',
    fontSize: '13px',
    marginTop: '14px',
  },
  footer: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '12px',
    marginTop: '32px',
    textAlign: 'center',
  },
};

// ─── Composant ───────────────────────────────────────────────────────────────
export default function FFMLogin({ onLogin }) {
  const navigate = useNavigate();

  // Étape 1 : saisie email | Étape 2 : saisie OTP
  const [etape, setEtape] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [chargement, setChargement] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'erreur'|'success', texte }

  // ── Étape 1 : envoyer le code OTP ─────────────────────────────────────────
  const handleEnvoyerCode = async (e) => {
    e.preventDefault();
    setMessage(null);
    const emailTrim = email.trim().toLowerCase();
    if (!emailTrim) return;

    setChargement(true);
    try {
      // Vérifier que l'email existe dans ffm_operateurs (actif)
      // Les onglets accessibles viennent du profil rattaché à l'opérateur
      const { data: operateur, error: errOp } = await supabase
        .from('ffm_operateurs')
        .select('id, prenom, nom, email, ffm_profils(onglets_accessibles)')
        .eq('email', emailTrim)
        .eq('actif', true)
        .single();

      if (errOp || !operateur) {
        setMessage({ type: 'erreur', texte: 'Aucun compte actif trouvé pour cet email.' });
        setChargement(false);
        return;
      }

      // Invalider les anciens codes non utilisés pour cet email
      await supabase
        .from('ffm_otp_sessions')
        .update({ utilise: true })
        .eq('email', emailTrim)
        .eq('utilise', false);

      // Générer et insérer le nouveau code
      const nouveauCode = genererCode();
      const { error: errInsert } = await supabase
        .from('ffm_otp_sessions')
        .insert({
          email: emailTrim,
          code: nouveauCode,
          operateur_id: operateur.id,
          expire_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        });

      if (errInsert) throw errInsert;

      // Envoyer l'email
      await sendEmail({
        to: emailTrim,
        subject: 'Votre code de connexion — Espace de gestion FFM',
        html: `
          <div style="font-family:sans-serif;max-width:480px;margin:auto;background:#f4f4f4;padding:32px 0">
            <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center">
            <table width="480" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08)">
              <tr><td style="background:#003189;padding:24px 40px;text-align:center">
                <table cellpadding="0" cellspacing="0" style="margin:0 auto 16px"><tr>
                  <td style="vertical-align:middle;padding-right:12px"><img src="https://factorylab3d.com/Image/Logo%20FFM%20blanc.png" alt="FFM" style="height:40px;display:block"></td>
                  <td style="vertical-align:middle;padding-right:12px"><span style="color:rgba(255,255,255,0.7);font-size:13px;font-family:Arial,sans-serif">Custom Lab FFM by</span></td>
                  <td style="vertical-align:middle"><img src="https://factorylab3d.com/Image/Logo%20Impulse%20blanc.svg" alt="Impulse" style="height:24px;display:block"></td>
                </tr></table>
                <h1 style="color:#ffffff;margin:0;font-size:22px;font-weight:700">Espace de gestion</h1>
                <p style="color:rgba(255,255,255,0.8);margin:8px 0 0;font-size:14px">Votre code de connexion</p>
              </td></tr>
              <tr><td style="padding:32px 40px">
                <p style="color:#1a1a2e;font-size:15px;margin:0 0 12px">Bonjour <strong>${operateur.prenom} ${operateur.nom}</strong>,</p>
                <p style="color:#374151;font-size:14px;margin:0 0 24px">Voici votre code de connexion à l'espace de gestion FFM :</p>
                <div style="text-align:center;margin:0 0 24px">
                  <span style="font-size:38px;font-weight:800;letter-spacing:12px;color:#003189">${nouveauCode}</span>
                </div>
                <p style="color:#6b7280;font-size:13px;margin:0">Ce code est valable <strong>10 minutes</strong>.<br>Si vous n'avez pas demandé ce code, ignorez cet email.</p>
              </td></tr>
              <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #e9ecef">
                <p style="font-size:12px;color:#adb5bd;margin:0">Fédération Française de Motocyclisme — Personnalisation des équipes de France</p>
              </td></tr>
            </table>
            </td></tr></table>
          </div>
        `,
      });

      setEtape(2);
      setMessage({ type: 'success', texte: `Code envoyé à ${emailTrim}` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'erreur', texte: "Une erreur s'est produite. Réessayez." });
    }
    setChargement(false);
  };

  // ── Étape 2 : vérifier le code OTP ────────────────────────────────────────
  const handleVerifierCode = async (e) => {
    e.preventDefault();
    setMessage(null);
    const codeTrim = code.trim();
    if (codeTrim.length !== 6) return;

    setChargement(true);
    try {
      const emailTrim = email.trim().toLowerCase();

      // Récupérer le code valide
      const { data: session, error: errSess } = await supabase
        .from('ffm_otp_sessions')
        .select('id, operateur_id, expire_at, utilise')
        .eq('email', emailTrim)
        .eq('code', codeTrim)
        .eq('utilise', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (errSess || !session) {
        setMessage({ type: 'erreur', texte: 'Code incorrect. Vérifiez et réessayez.' });
        setChargement(false);
        return;
      }

      if (new Date(session.expire_at) < new Date()) {
        setMessage({ type: 'erreur', texte: 'Ce code a expiré. Demandez un nouveau code.' });
        setChargement(false);
        return;
      }

      // Invalider le code
      await supabase
        .from('ffm_otp_sessions')
        .update({ utilise: true })
        .eq('id', session.id);

      // Récupérer l'opérateur complet avec son profil
      const { data: operateur, error: errOp } = await supabase
        .from('ffm_operateurs')
        .select('id, prenom, nom, email, ffm_profils(onglets_accessibles)')
        .eq('id', session.operateur_id)
        .single();

      if (errOp || !operateur) {
        setMessage({ type: 'erreur', texte: 'Erreur lors de la récupération du compte.' });
        setChargement(false);
        return;
      }

      // Sauvegarder la session FFM dans localStorage
      // Les rôles = onglets_accessibles du profil rattaché à l'opérateur
      const ffmSession = {
        id: operateur.id,
        prenom: operateur.prenom,
        nom: operateur.nom,
        email: operateur.email,
        roles: operateur.ffm_profils?.onglets_accessibles || ['commandes'],
        connectedAt: new Date().toISOString(),
      };
      localStorage.setItem('ffm_session', JSON.stringify(ffmSession));

      // Notifier App.js et rediriger
      if (onLogin) onLogin(ffmSession);
      navigate('/ffm-customlab/dashboard/commandes');
    } catch (err) {
      console.error(err);
      setMessage({ type: 'erreur', texte: "Une erreur s'est produite. Réessayez." });
    }
    setChargement(false);
  };

  return (
    <div style={S.page}>
      {/* Logo + titre */}
      <div style={S.logoWrap}>
        <img
          src="/Image/Logo FFM blanc.png"
          alt="FFM"
          style={S.logo}
        />
        <p style={S.title}>Custom Lab FFM by</p>
        <img
          src="/Image/Logo Impulse blanc.svg"
          alt="Impulse"
          style={S.logoImpulse}
        />
      </div>

      {/* Carte */}
      <div style={S.card}>
        {etape === 1 ? (
          <form onSubmit={handleEnvoyerCode}>
            <p style={S.cardTitle}>Connexion</p>
            <p style={S.cardSub}>
              Saisissez votre adresse email FFM pour recevoir un code de connexion à usage unique.
            </p>
            <label style={S.label}>Adresse email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              required
              style={S.input}
              onFocus={(e) => (e.target.style.borderColor = '#003189')}
              onBlur={(e) => (e.target.style.borderColor = '#D0D8EE')}
            />
            {message && (
              <div style={message.type === 'erreur' ? S.erreur : S.success}>
                {message.texte}
              </div>
            )}
            <button
              type="submit"
              disabled={chargement || !email.trim()}
              style={{ ...S.btn, ...(chargement || !email.trim() ? S.btnDisabled : {}) }}
            >
              {chargement ? 'Envoi en cours…' : 'Recevoir mon code de connexion'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifierCode}>
            <p style={S.cardTitle}>Vérification</p>
            <p style={S.cardSub}>
              Un code à 6 chiffres a été envoyé à <strong>{email}</strong>.<br />
              Saisissez-le ci-dessous.
            </p>
            <label style={S.label}>Code de connexion</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoFocus
              style={S.inputOtp}
            />
            {message && (
              <div style={message.type === 'erreur' ? S.erreur : S.success}>
                {message.texte}
              </div>
            )}
            <button
              type="submit"
              disabled={chargement || code.length !== 6}
              style={{ ...S.btn, ...(chargement || code.length !== 6 ? S.btnDisabled : {}) }}
            >
              {chargement ? 'Vérification…' : 'Se connecter'}
            </button>
            <button
              type="button"
              style={S.btnSecondary}
              onClick={() => {
                setEtape(1);
                setCode('');
                setMessage(null);
              }}
            >
              ← Utiliser une autre adresse email
            </button>
          </form>
        )}
      </div>

      <p style={S.footer}>© Impulse.racing</p>
    </div>
  );
}
